import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EpisodeCard from "@/components/feature/EpisodeCard";
import { episodes } from "@/mocks/episodes";
import { characters } from "@/mocks/characters";
import { useFavorites } from "@/hooks/useFavorites";

export default function EpisodeDetail() {
  const { id } = useParams<{ id: string }>();
  const episode = episodes.find((item) => item.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const related = useMemo(() => {
    if (!episode) return [];
    const byTag = episodes.filter(
      (item) => item.id !== episode.id && item.tags.some((tag) => episode.tags.includes(tag))
    );
    const fallback = episodes.filter((item) => item.id !== episode.id);
    const merged = [...byTag, ...fallback.filter((item) => !byTag.includes(item))];
    return merged.slice(0, 3);
  }, [episode]);

  const featuredCharacters = useMemo(() => {
    if (!episode) return [];
    return characters.filter((character) => character.appearances.includes(episode.id)).slice(0, 6);
  }, [episode]);

  if (!episode) {
    return (
      <div className="rounded-lg bg-background-50 border border-background-200 p-12 text-center">
        <title>该集剧情不存在 - 甜蜜惩罚动漫</title>
        <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400 mb-4">
          <i className="ri-file-unknow-line"></i>
        </span>
        <h1 className="font-heading text-xl text-foreground-950 mb-2">该集剧情不存在</h1>
        <p className="text-sm text-foreground-500 font-label mb-6">
          你访问的剧集可能已被移除或链接有误，返回全集列表重新选择吧。
        </p>
        <Link
          to="/episodes"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-label hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i> 返回全集剧情
        </Link>
      </div>
    );
  }

  const saved = isFavorite("episode", episode.id);

  const handleFavorite = () => {
    const nowSaved = toggleFavorite("episode", episode.id);
    setToast(nowSaved ? "已加入我的收藏" : "已取消收藏");
  };

  return (
    <div className="space-y-6">
      <title>第{episode.number}集 {episode.title} - 甜蜜惩罚动漫</title>
      <meta
        name="description"
        content={`甜蜜惩罚第${episode.number}集「${episode.title}」详细剧情介绍：${episode.summary}`}
      />
      <meta name="keywords" content={`甜蜜惩罚第${episode.number}集,甜蜜惩罚${episode.title},甜蜜惩罚剧情`} />
      <link rel="canonical" href={`https://cloudschoolserver.cn/episode/${episode.id}`} />

      <Breadcrumb
        items={[{ label: "首页", to: "/" }, { label: "全剧情", to: "/episodes" }, { label: `第${episode.number}集` }]}
      />

      <article className="rounded-lg bg-background-50 border border-background-200 overflow-hidden">
        <div className="relative w-full aspect-video bg-background-200">
          <img
            src={episode.cover}
            alt={`第${episode.number}集 ${episode.title} 官方剧照`}
            title={`甜蜜惩罚 第${episode.number}集 ${episode.title}`}
            className="w-full h-full object-cover object-top"
          />
          <span className="absolute top-3 left-3 px-3 py-1 rounded-md bg-primary-500 text-background-50 text-sm font-label">
            第 {episode.number} 集
          </span>
        </div>

        <div className="p-6 md:p-8">
          <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">
            第{episode.number}集：{episode.title}
          </h1>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm font-label text-foreground-600 mb-5">
            <span className="flex items-center gap-1.5">
              <i className="ri-play-list-2-line text-primary-600"></i> 集数：第 {episode.number} 集
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line text-primary-600"></i> 播放时长：{episode.duration}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line text-primary-600"></i> 发布日期：{episode.releaseDate}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-price-tag-3-line text-primary-600"></i> 剧情类型：{episode.tags.join(" · ")}
            </span>
          </div>

          <div className="flex flex-wrap gap-3 mb-6">
            <button
              type="button"
              onClick={handleFavorite}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-label transition-colors cursor-pointer whitespace-nowrap ${
                saved
                  ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                  : "bg-background-100 text-foreground-800 border border-background-300 hover:border-primary-300"
              }`}
            >
              <i className={saved ? "ri-heart-3-fill" : "ri-heart-3-line"}></i>
              {saved ? "已收藏本集" : "收藏本集"}
            </button>
            <Link
              to="/episodes"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-100 text-foreground-800 border border-background-300 text-sm font-label hover:border-primary-300 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i> 返回全集列表
            </Link>
          </div>

          <section className="mb-8">
            <h2 className="font-heading text-lg text-foreground-950 mb-3 flex items-center gap-2">
              <i className="ri-book-open-line text-primary-600"></i>剧情介绍
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-loose font-label mb-4">
              {episode.summary}
            </p>
            <p className="text-sm md:text-base text-foreground-600 leading-loose font-label">{episode.description}</p>
          </section>

          {featuredCharacters.length > 0 && (
            <section className="mb-8">
              <h2 className="font-heading text-lg text-foreground-950 mb-3 flex items-center gap-2">
                <i className="ri-user-star-line text-primary-600"></i>本集出场角色
              </h2>
              <div className="flex flex-wrap gap-2">
                {featuredCharacters.map((character) => (
                  <Link
                    key={character.id}
                    to={`/character/${character.id}`}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-sm font-label hover:bg-accent-100 hover:text-accent-900 transition-colors cursor-pointer"
                  >
                    <i className="ri-user-3-line"></i>
                    {character.name}
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>

      <section>
        <h2 className="font-heading text-xl text-foreground-950 mb-4 flex items-center gap-2">
          <i className="ri-magic-line text-accent-600"></i>相关剧情推荐
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {related.map((item, index) => (
            <EpisodeCard key={item.id} episode={item} index={index} />
          ))}
        </div>
      </section>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-md bg-foreground-950 text-background-50 text-sm font-label animate-fade-up">
          <i className="ri-checkbox-circle-line mr-1.5 text-accent-400"></i>
          {toast}
        </div>
      )}
    </div>
  );
}